<template>
  <woot-dropdown-menu>
    <template v-if="showCapacity">
      <woot-dropdown-item class="flex flex-col gap-1 px-2 py-1">
        <span
          class="text-xs font-medium text-slate-600 dark:text-slate-300"
          :class="{ 'text-red-500 dark:text-red-400': atCapacity }"
        >
          {{ $t('QUEUES_MGMT.CAPACITY.LABEL') }}: {{ capacityLabel }}
        </span>
        <!-- QM §5.10 — the bar is decoration; the label above always carries the value -->
        <div
          v-if="capacityPercent !== null"
          class="h-1.5 w-full rounded bg-slate-100 dark:bg-slate-800 overflow-hidden"
        >
          <div
            class="h-full rounded"
            :class="atCapacity ? 'bg-red-500' : 'bg-woot-500'"
            :style="{ width: `${capacityPercent}%` }"
          />
        </div>
      </woot-dropdown-item>
      <woot-dropdown-divider />
    </template>
    <woot-dropdown-header :title="$t('SIDEBAR.SET_AVAILABILITY_TITLE')" />
    <woot-dropdown-item
      v-for="status in availabilityStatuses"
      :key="status.value"
      class="flex items-baseline"
    >
      <woot-button
        size="small"
        :color-scheme="status.disabled ? '' : 'secondary'"
        :variant="status.disabled ? 'smooth' : 'clear'"
        class-names="status-change--dropdown-button"
        @click="changeAvailabilityStatus(status.value)"
      >
        <availability-status-badge :status="status.value" />
        {{ status.label }}
      </woot-button>
    </woot-dropdown-item>
    <template v-if="hasReadPermissionNew('Auto Presence')">
      <woot-dropdown-divider />
      <woot-dropdown-item class="m-0 flex items-center justify-between p-2">
        <div class="flex items-center">
          <fluent-icon
            v-tooltip.right-start="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_TEXT')"
            icon="info"
            size="14"
            class="mt-px"
          />

          <span
            class="my-0 mx-1 text-xs font-medium text-slate-600 dark:text-slate-100"
          >
            {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
          </span>
        </div>

        <woot-switch
          size="small"
          class="mt-px mx-1 mb-0"
          :value="currentUserAutoOffline"
          :disabled="!hasEditPermissionNew('Auto Presence')"
          @input="updateAutoOffline"
        />
      </woot-dropdown-item>
      <woot-dropdown-divider />
    </template>
  </woot-dropdown-menu>
</template>

<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import alertMixin from 'shared/mixins/alertMixin';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import WootDropdownHeader from 'shared/components/ui/dropdown/DropdownHeader.vue';
import WootDropdownDivider from 'shared/components/ui/dropdown/DropdownDivider.vue';
import AvailabilityStatusBadge from '../widgets/conversation/AvailabilityStatusBadge.vue';
import wootConstants from 'dashboard/constants/globals';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

export default {
  components: {
    WootDropdownHeader,
    WootDropdownDivider,
    WootDropdownMenu,
    WootDropdownItem,
    AvailabilityStatusBadge,
  },

  mixins: [clickaway, alertMixin, hasPermissionMixin],

  data() {
    return {
      isStatusMenuOpened: false,
      isUpdating: false,
    };
  },

  computed: {
    ...mapGetters({
      getCurrentUserAvailability: 'getCurrentUserAvailability',
      currentAccountId: 'getCurrentAccountId',
      currentUserAutoOffline: 'getCurrentUserAutoOffline',
      currentUser: 'getCurrentUser',
    }),
    // QM §5.4.1 — agent capacity indicator (X / cap). Populated only when queue_management is on.
    queueCapacity() {
      return this.currentUser?.queue_capacity;
    },
    showCapacity() {
      const enabled = this.$store.getters['accounts/isFeatureEnabledonAccount'](
        this.currentAccountId,
        FEATURE_FLAGS.QUEUE_MANAGEMENT
      );
      return enabled && !!this.queueCapacity;
    },
    capacityLabel() {
      const { open_count: open, cap } = this.queueCapacity || {};
      const base = `${open ?? 0} / ${cap ?? '∞'}`;
      return this.atCapacity
        ? `${base} · ${this.$t('QUEUES_MGMT.CAPACITY.AT_CAP')}`
        : base;
    },
    atCapacity() {
      const { open_count: open, cap } = this.queueCapacity || {};
      return cap != null && open != null && open >= cap;
    },
    // QM §5.4.1 — bar width; null (no bar) when cap is unlimited/unknown
    capacityPercent() {
      const { open_count: open, cap } = this.queueCapacity || {};
      if (cap == null || open == null) return null;
      return Math.min((open / cap) * 100, 100);
    },
    availabilityDisplayLabel() {
      const availabilityIndex = AVAILABILITY_STATUS_KEYS.findIndex(
        key => key === this.currentUserAvailability
      );
      return this.$t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUSES_LIST')[
        availabilityIndex
      ];
    },
    currentUserAvailability() {
      return this.getCurrentUserAvailability;
    },
    availabilityStatuses() {
      return this.$t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUSES_LIST').map(
        (statusLabel, index) => ({
          label: statusLabel,
          value: AVAILABILITY_STATUS_KEYS[index],
          disabled:
            this.currentUserAvailability === AVAILABILITY_STATUS_KEYS[index],
        })
      );
    },
  },

  methods: {
    openStatusMenu() {
      this.isStatusMenuOpened = true;
    },
    closeStatusMenu() {
      this.isStatusMenuOpened = false;
    },
    updateAutoOffline(autoOffline) {
      this.$store.dispatch('updateAutoOffline', {
        accountId: this.currentAccountId,
        autoOffline,
      });
    },
    changeAvailabilityStatus(availability) {
      if (this.isUpdating) {
        return;
      }

      this.isUpdating = true;
      try {
        this.$store.dispatch('updateAvailability', {
          availability,
          account_id: this.currentAccountId,
        });
      } catch (error) {
        this.showAlert(
          this.$t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR')
        );
      } finally {
        this.isUpdating = false;
      }
    },
  },
};
</script>
